import { Controller } from '@hotwired/stimulus'
import consumer from '~/channels/consumer'

/**
 * Uptime Status Controller
 *
 * Handles real-time uptime status updates via ActionCable.
 * Subscribed to the UptimeChannel for live status changes.
 */
export default class extends Controller {
  static targets = [
    'badge',
    'toggleButton',
    'uptimePercentage',
    'avgResponse',
    'lastChecked'
  ]

  static values = {
    websiteId: String,
    monitorEnabled: Boolean
  }

  connect() {
    console.log('[UptimeStatus] Connected for website:', this.websiteIdValue)
    if (this.monitorEnabledValue) {
      this.subscribeToChannel()
    }
  }

  disconnect() {
    this.unsubscribeFromChannel()
  }

  // ==================== ActionCable ====================

  subscribeToChannel() {
    this.subscription = consumer.subscriptions.create(
      { channel: 'UptimeChannel', website_id: this.websiteIdValue },
      {
        connected: () => console.log('[UptimeStatus] ActionCable connected'),
        disconnected: () => console.log('[UptimeStatus] ActionCable disconnected'),
        received: (data) => this.handleBroadcast(data)
      }
    )
  }

  unsubscribeFromChannel() {
    if (this.subscription) {
      this.subscription.unsubscribe()
      this.subscription = null
    }
  }

  handleBroadcast(data) {
    console.log('[UptimeStatus] Broadcast:', data)

    if (data.event === 'status_update') {
      this.updateStatus(data)
    }
  }

  // ==================== UI Updates ====================

  updateStatus(data) {
    // Update badge
    if (this.hasBadgeTarget && data.status) {
      const statusConfig = this.getStatusConfig(data.status)
      this.badgeTarget.innerHTML = `
        <span class="inline-block w-2 h-2 rounded-full ${statusConfig.dotClass} mr-1"></span>
        ${statusConfig.label}
      `
      this.badgeTarget.className = `badge ${statusConfig.badgeClass}`
    }

    // Update uptime percentage
    if (this.hasUptimePercentageTarget && data.uptime_percentage !== undefined) {
      this.uptimePercentageTarget.textContent =
        data.uptime_percentage !== null ?
          `${data.uptime_percentage.toFixed(2)}%` :
          'N/A'
    }

    // Update avg response time
    if (this.hasAvgResponseTarget && data.avg_response_time !== undefined) {
      this.avgResponseTarget.textContent = this.formatResponseTime(data.avg_response_time)
    }

    // Update last checked
    if (this.hasLastCheckedTarget && data.last_checked_at) {
      this.lastCheckedTarget.textContent = this.timeAgo(new Date(data.last_checked_at))
    }
  }

  getStatusConfig(status) {
    const configs = {
      up: {
        label: 'Operational',
        badgeClass: 'badge-success',
        dotClass: 'bg-green-500 animate-pulse'
      },
      down: {
        label: 'Down',
        badgeClass: 'badge-error',
        dotClass: 'bg-red-500'
      },
      degraded: {
        label: 'Degraded',
        badgeClass: 'badge-warning',
        dotClass: 'bg-yellow-500'
      },
      unknown: {
        label: 'Unknown',
        badgeClass: 'badge-default',
        dotClass: 'bg-gray-400'
      }
    }
    return configs[status] || configs.unknown
  }

  formatResponseTime(ms) {
    if (!ms) return 'N/A'
    if (ms < 1000) return `${Math.round(ms)}ms`
    return `${(ms / 1000).toFixed(1)}s`
  }

  timeAgo(date) {
    const seconds = Math.floor((new Date() - date) / 1000)

    if (seconds < 60) return 'Just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`
    return `${Math.floor(seconds / 86400)} days ago`
  }
}
